import React, { useMemo } from 'react';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
// Pont jetons CSS → canvas : les graphiques lisent la charte au lieu de figer
// des couleurs. Le jour où la charte bascule, ils suivent sans être retouchés.
import { jetons, alpha, optionsBase, fusionner } from '../../components/ndoe/chartTheme';
import './mobileCharts.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler, Tooltip, Legend);

const integer = (value) => Number(value || 0).toLocaleString('fr-FR');

/*
  Socle commun aux trois graphiques du cockpit. C'est une FONCTION, pas une
  constante : optionsBase() lit les jetons via getComputedStyle, et au chargement
  du module le CSS n'est pas garanti appliqué — les couleurs seraient lues vides
  et le pont retomberait EN SILENCE sur ses valeurs de repli. Appelée dans le
  rendu, elle lit la charte réellement peinte.

  Les trois graphiques comptent tous des paires : l'info-bulle porte cette unité
  et n'affiche pas de pastille de couleur (une seule série, elle n'apprend rien).
*/
const optionsPaires = ({ axeY = true } = {}) => fusionner(optionsBase({ axeY }), {
  plugins: {
    legend: { display: false },
    tooltip: {
      displayColors: false,
      callbacks: {
        label: (context) => `${integer(context.parsed.y ?? context.parsed.x)} paire(s)`,
      },
    },
  },
});

const ChartFrame = ({ title, caption, children, ariaLabel }) => (
  <section className="mobile-chart-card" aria-label={ariaLabel || title}>
    <div className="mobile-chart-head"><div><span>ANALYSE VISUELLE</span><h3>{title}</h3><p>{caption}</p></div></div>
    <div className="mobile-chart-canvas" role="img" aria-label={ariaLabel || title}>{children}</div>
  </section>
);

/*
  Barres HORIZONTALES (indexAxis: 'y') : l'axe des VALEURS est x, l'axe des
  CATÉGORIES est y — l'inverse de ce que pose optionsBase. D'où les grilles
  réaffirmées ici : `display: true` sur x est indispensable, car la fusion
  hériterait sinon du `display: false` que la base met sur l'axe des catégories.
*/
const echellesBarresHorizontales = (t) => ({
  x: {
    beginAtZero: true,
    grid: { display: true, color: t.hairline },
    ticks: { precision: 0, color: t.muted, font: { size: 10, weight: '600' } },
  },
  y: {
    grid: { display: false },
    ticks: { color: t.text, font: { size: 10, weight: '700' } },
  },
});

export const TodayModelChart = ({ items = [] }) => {
  const rows = useMemo(() => items.slice(0, 5), [items]);
  // Jetons lus DANS le rendu (voir optionsPaires) : au niveau module, le CSS
  // n'est pas encore appliqué.
  const t = jetons();
  if (!rows.length) return <ChartFrame title="Ventes du jour par modèle" caption="Aucune vente nette confirmée aujourd’hui."><div className="mobile-chart-empty">Les ventes confirmées du jour apparaîtront ici.</div></ChartFrame>;
  return <ChartFrame title="Ventes du jour par modèle" caption="Top des modèles, ventes confirmées moins retours clientes." ariaLabel="Graphique des ventes nettes du jour par modèle">
    {/* Or de la charte : le jour est la maille « chaude » du cockpit, et cela
        distingue ces barres de celles, bleues, du classement par livreur. */}
    <Bar
      data={{ labels: rows.map((item) => item.nom_produit), datasets: [{ data: rows.map((item) => Number(item.net_pairs || 0)), borderRadius: 8, borderSkipped: false, backgroundColor: t.gold }] }}
      options={fusionner(optionsPaires(), { indexAxis: 'y', scales: echellesBarresHorizontales(t) })}
    />
  </ChartFrame>;
};

export const SalesTrendChart = ({ items = [] }) => {
  const rows = useMemo(() => items, [items]);
  const t = jetons();
  if (rows.length < 2) return null;
  return <ChartFrame title="Évolution des ventes nettes" caption="La ligne suit la période sélectionnée ; les retours clientes sont déduits le jour de leur enregistrement." ariaLabel="Graphique de l'évolution des ventes nettes">
    {/* Ligne bleu royal, points dorés cerclés de papier : le cerclage détache
        chaque point de son aire translucide, sinon les jours creux s'y noient. */}
    <Line
      data={{ labels: rows.map((item) => item.label), datasets: [{ data: rows.map((item) => Number(item.net_pairs || 0)), tension: 0.35, fill: true, borderColor: t.royal, backgroundColor: alpha(t.royal, 0.10), pointBackgroundColor: t.gold, pointBorderColor: t.paper, pointBorderWidth: 2, pointRadius: 3, pointHoverRadius: 4 }] }}
      options={fusionner(optionsPaires(), {
        scales: {
          x: { grid: { display: false }, ticks: { color: t.muted, font: { size: 10, weight: '600' }, maxRotation: 0, autoSkip: true, maxTicksLimit: 8 } },
          y: { beginAtZero: true, grid: { color: t.hairline }, ticks: { precision: 0, color: t.muted, font: { size: 10, weight: '600' } } },
        },
      })}
    />
  </ChartFrame>;
};

export const DeliveryPerformanceChart = ({ items = [] }) => {
  const rows = useMemo(() => items.slice(0, 6), [items]);
  const t = jetons();
  if (!rows.length) return null;
  return <ChartFrame title="Paires nettes vendues par livreur" caption="Comparaison sur la période choisie ; les retours clientes attribuables sont déduits." ariaLabel="Graphique des paires nettes vendues par livreur">
    {/* Bleu royal, comme la courbe d'évolution : ces barres et elle mesurent la
        même chose (des paires nettes vendues), l'or restant réservé au jour. */}
    <Bar
      data={{ labels: rows.map((item) => item.delivery_person), datasets: [{ data: rows.map((item) => Number(item.net_sold || 0)), borderRadius: 8, borderSkipped: false, backgroundColor: t.royal }] }}
      options={fusionner(optionsPaires(), { indexAxis: 'y', scales: echellesBarresHorizontales(t) })}
    />
  </ChartFrame>;
};
